import { useEffect, useState } from "react";
import { Printer, FileText, Filter, Users, UserMinus, BookOpenCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { GRADES, ACADEMIC_YEARS } from "../lib/constants";
import { listNewStudents, listWithdrawnStudents, getStats } from "../lib/api";
import { printHtmlReport } from "../lib/pdf";

export default function Reports() {
  const [grade, setGrade] = useState("all");
  const [year, setYear] = useState("all");
  const [stats, setStats] = useState(null);

  useEffect(() => { getStats().then(setStats).catch(() => {}); }, []);

  const buildParams = () => {
    const p = {};
    if (grade !== "all") p.grade = grade;
    if (year !== "all") p.academic_year = year;
    return p;
  };

  const printNew = async () => {
    const rows = await listNewStudents(buildParams());
    printHtmlReport({
      title: "تقرير الطلاب المستجدين",
      columns: [
        { key: "name", header: "اسم الطالب" },
        { key: "national_id", header: "رقم الهوية" },
        { key: "phone", header: "رقم الجوال" },
        { key: "grade", header: "الصف" },
        { key: "section", header: "الفصل" },
        { key: "academic_year", header: "العام الدراسي" },
        { key: "registration_date", header: "تاريخ التسجيل" },
      ],
      rows,
      meta: [
        { label: "الصف", value: grade === "all" ? "الكل" : grade },
        { label: "العام الدراسي", value: year === "all" ? "الكل" : year },
      ]
    });
  };

  const printWithdrawn = async () => {
    const rows = await listWithdrawnStudents(buildParams());
    printHtmlReport({
      title: "تقرير الطلاب المنسحبين",
      columns: [
        { key: "name", header: "اسم الطالب" },
        { key: "national_id", header: "رقم الهوية" },
        { key: "grade", header: "الصف" },
        { key: "section", header: "الفصل" },
        { key: "academic_year", header: "العام الدراسي" },
        { key: "withdrawal_date", header: "تاريخ الانسحاب" },
      ],
      rows,
      meta: [
        { label: "الصف", value: grade === "all" ? "الكل" : grade },
        { label: "العام الدراسي", value: year === "all" ? "الكل" : year },
      ]
    });
  };

  const printCombined = async () => {
    const newRows = await listNewStudents(buildParams());
    const wRows = await listWithdrawnStudents(buildParams());
    const combined = [
      ...newRows.map(r => ({ ...r, type: "مستجد", date: r.registration_date })),
      ...wRows.map(r => ({ ...r, type: "منسحب", date: r.withdrawal_date })),
    ];
    printHtmlReport({
      title: "التقرير الشامل — المستجدون والمنسحبون",
      columns: [
        { key: "type", header: "الحالة" },
        { key: "name", header: "اسم الطالب" },
        { key: "national_id", header: "رقم الهوية" },
        { key: "grade", header: "الصف" },
        { key: "academic_year", header: "العام الدراسي" },
        { key: "date", header: "التاريخ" },
      ],
      rows: combined,
      meta: [
        { label: "الصف", value: grade === "all" ? "الكل" : grade },
        { label: "العام الدراسي", value: year === "all" ? "الكل" : year },
        { label: "المستجدون", value: newRows.length },
        { label: "المنسحبون", value: wRows.length },
      ]
    });
  };

  const printGradeReport = () => {
    if (!stats) return;
    printHtmlReport({
      title: "تقرير التوزيع حسب الصفوف",
      columns: [
        { key: "grade", header: "الصف" },
        { key: "new", header: "عدد المستجدين" },
        { key: "withdrawn", header: "عدد المنسحبين" },
        { key: "net", header: "الصافي" },
      ],
      rows: stats.by_grade.map(g => ({ ...g, net: g.new - g.withdrawn })),
      meta: [{ label: "العام", value: "جميع الأعوام" }]
    });
  };

  return (
    <div>
      <PageHeader title="التقارير والطباعة" subtitle="إنشاء تقارير مفصلة قابلة للطباعة وحفظها كملف PDF" icon={FileText} />

      <Card className="p-5 mb-5 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={16} className="text-teal-700" />
          <h3 className="font-cairo font-bold text-slate-900">عوامل التصفية</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>الصف الدراسي</Label>
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger className="bg-white h-11" dir="rtl" data-testid="reports-grade"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع الصفوف</SelectItem>
                {GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>العام الدراسي</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="bg-white h-11" dir="rtl" data-testid="reports-year"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع الأعوام</SelectItem>
                {ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <ReportCard
          icon={Users}
          title="تقرير المستجدين"
          desc="قائمة الطلاب المسجلين الجدد"
          onClick={printNew}
          color="teal"
          testid="report-new"
        />
        <ReportCard
          icon={UserMinus}
          title="تقرير المنسحبين"
          desc="قائمة الطلاب الذين انسحبوا"
          onClick={printWithdrawn}
          color="rose"
          testid="report-withdrawn"
        />
        <ReportCard
          icon={FileText}
          title="التقرير الشامل"
          desc="جميع الطلاب المستجدين والمنسحبين"
          onClick={printCombined}
          color="slate"
          testid="report-combined"
        />
        <ReportCard
          icon={BookOpenCheck}
          title="تقرير الصفوف"
          desc="توزيع الطلاب على الصفوف"
          onClick={printGradeReport}
          color="gold"
          testid="report-grades"
        />
      </div>

      <Card className="p-5 mt-5 border-slate-200 bg-slate-50/60">
        <div className="flex items-start gap-3 text-sm text-slate-600">
          <div className="w-9 h-9 rounded-lg bg-teal-700 text-white flex items-center justify-center shrink-0"><Printer size={16} /></div>
          <div>
            <div className="font-cairo font-bold text-slate-800 mb-1">طريقة حفظ التقرير كـ PDF</div>
            <p className="leading-relaxed">عند الضغط على أي تقرير، ستفتح نافذة الطباعة. اختر <span className="font-bold text-teal-700">{`"حفظ كملف PDF"`}</span> من قائمة الطابعة لتصدير التقرير.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ReportCard({ icon: Icon, title, desc, onClick, color, testid }) {
  const colors = {
    teal: "from-teal-600 to-teal-700",
    rose: "from-rose-500 to-rose-600",
    slate: "from-slate-700 to-slate-800",
    gold: "from-amber-500 to-yellow-600",
  };
  return (
    <button onClick={onClick} className="text-right group" data-testid={testid}>
      <div className={`rounded-2xl bg-gradient-to-br ${colors[color]} p-5 text-white btn-elevate shadow-lg`}>
        <div className="flex items-center justify-between mb-4">
          <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center"><Icon size={20} /></div>
          <Printer size={18} className="opacity-70" />
        </div>
        <div className="font-cairo text-lg font-bold">{title}</div>
        <div className="text-sm opacity-90 mt-1">{desc}</div>
      </div>
    </button>
  );
}
